import {
  Download as DownloadIcon,
  Payment as PaymentIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { format } from "date-fns-jalali";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchInvoices } from "../../../application/actions/financialActions";
import {
  selectInvoicesData,
  selectInvoicesLoading,
} from "../../../application/selectors/financial";

const InvoiceList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const invoices = useSelector(selectInvoicesData);
  const loading = useSelector(selectInvoicesLoading);

  // Local State
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    dispatch(fetchInvoices());
  }, [dispatch]);

  const getInvoiceStatusColor = (status) => {
    switch (status) {
      case "Draft":
        return "default";
      case "Pending":
        return "warning";
      case "Paid":
        return "success";
      case "Overdue":
        return "error";
      case "Cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getInvoiceStatusLabel = (status) => {
    const labels = {
      Draft: "پیش‌نویس",
      Pending: "در انتظار پرداخت",
      Paid: "پرداخت شده",
      Overdue: "سررسید گذشته",
      Cancelled: "لغو شده",
    };
    return labels[status] || status;
  };

  const handleViewDetails = (invoiceId) => {
    navigate(`/invoices/${invoiceId}`);
  };

  const handlePayment = (invoiceId) => {
    navigate(`/payment/${invoiceId}`);
  };

  const handleDownload = (invoiceId) => {
    // TODO: Implement download functionality
    console.log("Download invoice:", invoiceId);
  };

  const handlePrint = (invoiceId) => {
    // TODO: Implement print functionality
    window.print();
  };

  const filteredInvoices =
    statusFilter === "all"
      ? invoices
      : invoices?.filter((invoice) => invoice.status === statusFilter);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          فاکتورها
        </Typography>
        <Typography variant="body1" color="text.secondary">
          مشاهده و مدیریت فاکتورهای خود
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>فیلتر بر اساس وضعیت</InputLabel>
              <Select
                value={statusFilter}
                label="فیلتر بر اساس وضعیت"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">همه فاکتورها</MenuItem>
                <MenuItem value="Pending">در انتظار پرداخت</MenuItem>
                <MenuItem value="Paid">پرداخت شده</MenuItem>
                <MenuItem value="Overdue">سررسید گذشته</MenuItem>
                <MenuItem value="Cancelled">لغو شده</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="body2" color="text.secondary">
              {filteredInvoices?.length || 0} فاکتور یافت شد
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Invoices List */}
      {!loading && (
        <>
          {filteredInvoices && filteredInvoices.length > 0 ? (
            <Grid container spacing={3}>
              {filteredInvoices.map((invoice) => (
                <Grid item xs={12} key={invoice.id}>
                  <Card
                    sx={{
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        {/* Invoice Number & Date */}
                        <Grid item xs={12} md={3}>
                          <Typography variant="h6" gutterBottom>
                            فاکتور #{invoice.invoiceNumber}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {invoice.createdAt &&
                              format(new Date(invoice.createdAt), "yyyy/MM/dd")}
                          </Typography>
                        </Grid>

                        {/* Status */}
                        <Grid item xs={12} md={2}>
                          <Chip
                            label={getInvoiceStatusLabel(invoice.status)}
                            color={getInvoiceStatusColor(invoice.status)}
                            sx={{ fontWeight: "bold" }}
                          />
                        </Grid>

                        {/* Order Reference */}
                        {invoice.orderNumber && (
                          <Grid item xs={12} md={2}>
                            <Typography variant="body2" color="text.secondary">
                              سفارش
                            </Typography>
                            <Typography variant="body1">
                              #{invoice.orderNumber}
                            </Typography>
                          </Grid>
                        )}

                        {/* Due Date */}
                        {invoice.dueDate && (
                          <Grid item xs={12} md={2}>
                            <Typography variant="body2" color="text.secondary">
                              سررسید
                            </Typography>
                            <Typography
                              variant="body1"
                              color={
                                invoice.status === "Overdue"
                                  ? "error"
                                  : "text.primary"
                              }
                            >
                              {format(new Date(invoice.dueDate), "yyyy/MM/dd")}
                            </Typography>
                          </Grid>
                        )}

                        {/* Total Amount */}
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="text.secondary">
                            مبلغ کل
                          </Typography>
                          <Typography
                            variant="h6"
                            color="primary"
                            fontWeight="bold"
                          >
                            {invoice.totalAmount?.toLocaleString("fa-IR")} ریال
                          </Typography>
                        </Grid>

                        {/* Actions */}
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                              pt: 2,
                              borderTop: 1,
                              borderColor: "divider",
                            }}
                          >
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ViewIcon />}
                              onClick={() => handleViewDetails(invoice.id)}
                            >
                              جزئیات
                            </Button>

                            {invoice.status === "Pending" && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<PaymentIcon />}
                                onClick={() => handlePayment(invoice.id)}
                              >
                                پرداخت
                              </Button>
                            )}

                            {invoice.status === "Paid" && (
                              <>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<DownloadIcon />}
                                  onClick={() => handleDownload(invoice.id)}
                                >
                                  دانلود
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<PrintIcon />}
                                  onClick={() => handlePrint(invoice.id)}
                                >
                                  چاپ
                                </Button>
                              </>
                            )}
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Payment Info */}
                      {invoice.status === "Paid" && invoice.paymentDate && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            backgroundColor: "success.light",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2">
                            <strong>پرداخت شده در:</strong>{" "}
                            {format(
                              new Date(invoice.paymentDate),
                              "yyyy/MM/dd - HH:mm"
                            )}
                          </Typography>
                          {invoice.paymentMethod && (
                            <Typography variant="body2">
                              <strong>روش پرداخت:</strong>{" "}
                              {invoice.paymentMethod}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                فاکتوری یافت نشد
              </Typography>
              <Typography variant="body2" color="text.secondary">
                هنوز فاکتوری صادر نشده است
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* Summary Cards */}
      {invoices && invoices.length > 0 && (
        <Grid container spacing={3} sx={{ mt: 4 }}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  کل فاکتورها
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {invoices.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  در انتظار پرداخت
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {invoices.filter((inv) => inv.status === "Pending").length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  پرداخت شده
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {invoices.filter((inv) => inv.status === "Paid").length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default InvoiceList;
